import type { ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import CarouselCardMedium from 'components/OfferList/OfferCards/OfferCardMedium/CarouselCardMedium'
import { mediaQueryUp } from 'components/utils/breakpoint'

const CardContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: ${rem(4)};
  overflow: hidden;
`

const CardContainerNoHeader = styled(CardContainer)`
  padding-top: 0;
`

const MatchHeader = styled.div<{ $compact?: boolean }>`
  width: 100%;
  min-height: ${props => rem(props.$compact ? 64 : 80)};
  max-height: ${props => rem(props.$compact ? 64 : 80)};
  overflow: hidden;
  padding: 0 ${props => rem(props.$compact ? 10 : 12)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    min-height: ${props => rem(props.$compact ? 72 : 88)};
    max-height: ${props => rem(props.$compact ? 72 : 88)};
  }
`

const MatchLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  width: 100%;
  height: 100%;
  min-width: 0;
`

const MatchText = styled(BodyText)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`

const MatchIcon = styled.span`
  display: inline-flex;
  vertical-align: text-bottom;
  margin-right: ${rem(4)};
`

const MatchLabelText = styled.span<{ $compact?: boolean }>`
  background: ${props => props.theme.palette.product.ai.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  font-size: ${props => rem(props.$compact ? 12 : 14)};
`

const MatchDescriptionText = styled.span<{ $compact?: boolean }>`
  color: ${props => props.theme.palette.neutral.default.one};
  margin-left: ${rem(4)};
  font-size: ${props => rem(props.$compact ? 12 : 13)};
`

export const CardBody = styled.div`
  width: 100%;
`

const ErrorCard = styled.div<{ $compact?: boolean }>`
  width: 100%;
  min-height: ${props => rem(props.$compact ? 220 : 400)};
  padding: ${props => rem(props.$compact ? 16 : 24)};
  background: ${props => props.theme.palette.messaging.warning.lightBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    min-height: ${props => rem(props.$compact ? 260 : 520)};
  }
`

export const CardMetaText = styled.div`
  min-width: 0;
  max-width: 100%;
`

export const CardMetaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-start;
  gap: ${rem(8)};
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`

const InlineBestMatchTag = styled.div<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-self: end;
  gap: ${rem(4)};
  white-space: nowrap;
  border-radius: ${rem(999)};
  padding: ${props => props.$compact ? `${rem(2)} ${rem(6)}` : `${rem(2)} ${rem(8)}`};
  background: ${props => props.theme.palette.neutral.default.six};
`

const InlineBestMatchText = styled.span<{ $compact?: boolean }>`
  background: ${props => props.theme.palette.product.ai.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  font-size: ${props => rem(props.$compact ? 11 : 12)};
`

/*
 * LesaCarouselCardMedium overrides CarouselCardMedium internals via structural
 * selectors because that component does not expose slot-level styling hooks.
 * The layout this targets is:
 *   > :first-child  — image/media section
 *     > div:last-child — top-right overlay row containing .right (bookmark /
 *                       other floating actions) which we reposition.
 *   > :last-child   — content section (everything below the image)
 *     > :first-child  — title + meta stack
 *       > :first-child — title/location row
 *         > :first-child — inner title wrapper that needs to shrink
 *     > :last-child   — price / footer row pinned to the bottom
 */
export const LesaCarouselCardMedium = styled(CarouselCardMedium)<{ $compact?: boolean }>`
  width: 100%;
  min-height: ${props => rem(props.$compact ? 232 : 280)};
  gap: ${props => rem(props.$compact ? 12 : 16)};

  /* Content section: stretch to fill available card height so the footer pins. */
  > :last-child {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 ${props => rem(props.$compact ? 10 : 12)};
  }

  /* Price / CTA row — push to the bottom of the content section. */
  > :last-child > :last-child {
    width: 100%;
    margin-top: auto;
    padding-top: ${props => rem(props.$compact ? 10 : 0)};
  }

  /* Title/meta row must allow children to truncate instead of expanding. */
  > :last-child > :first-child > :first-child {
    width: 100%;
    min-width: 0;
  }

  /* Inner title wrapper — same truncation reasoning as the parent. */
  > :last-child > :first-child > :first-child > :first-child {
    width: 100%;
    min-width: 0;
  }

  /*
   * NumberRating is hidden to match the Figma design. We lean on [data-testid] 
   * because the rating component does not expose a class/prop to opt out of this element; 
   * if a prop is ever added, switch to that and drop the test-id coupling.
   */
  [data-testid="number-rating"] > :last-child {
    display: none;
  }

  /* .right is the top-right overlay row on the media — reposition the
     BookmarkButton etc. over the image. */
  > :first-child > div:last-child > .right {
    position: absolute;
    top: ${props => rem(props.$compact ? 6 : 8)};
    right: ${props => rem(props.$compact ? 8 : 12)};
  }

  > :first-child > div:last-child {
    padding: ${props => props.$compact ? `${rem(6)} ${rem(8)}` : `${rem(8)} ${rem(12)}`};
  }

  > :last-child > :first-child {
    gap: ${props => rem(props.$compact ? 6 : 8)};
  }

  ${mediaQueryUp.tablet} {
    min-height: ${props => rem(props.$compact ? 256 : 360)};

    > :last-child {
      padding: 0 ${props => rem(props.$compact ? 10 : 16)};
    }
  }
`

export function NlsCardContainer({
  children,
  useInlineBestMatchTag,
}: {
  children: ReactNode
  useInlineBestMatchTag: boolean
}) {
  const ContainerComponent = useInlineBestMatchTag ? CardContainerNoHeader : CardContainer
  return <ContainerComponent>{children}</ContainerComponent>
}

export function NlsMatchHeader({
  compact = false,
  heading,
  reasonText,
}: {
  compact?: boolean
  heading: string
  reasonText?: string
}) {
  return (
    <MatchHeader $compact={compact}>
      <MatchLabel>
        <MatchText variant="small" lineClamp={compact ? 3 : 4}>
          <MatchIcon>
            <AISparkleIcon size="S" />
          </MatchIcon>
          <MatchLabelText $compact={compact}>{heading}</MatchLabelText>
          {!!reasonText && <MatchDescriptionText $compact={compact}>{reasonText}</MatchDescriptionText>}
        </MatchText>
      </MatchLabel>
    </MatchHeader>
  )
}

export function InlineBestMatchPill({ compact = false, heading }: { compact?: boolean, heading: string }) {
  return (
    <InlineBestMatchTag $compact={compact}>
      <AISparkleIcon size="S" />
      <InlineBestMatchText $compact={compact}>{heading}</InlineBestMatchText>
    </InlineBestMatchTag>
  )
}

export function NlsErrorCard({ compact = false, message }: { compact?: boolean, message: string }) {
  return (
    <ErrorCard $compact={compact}>
      <LineExclamationTriangleIcon size="L" colour="warning" />
      <CaptionTextBlock variant="medium" colour="light-foreground-alt">
        {message}
      </CaptionTextBlock>
    </ErrorCard>
  )
}
